import _ from 'lodash';
import { history } from 'umi';

import { bindServices } from '@/services/api';
import SocketService from '@/services/socket.io';

const api = bindServices('processes');
import { formFieldBehaviorsMapping } from '@/utils/core/business-logic';
import { toPaginate } from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import CoreQueries from '@/utils/input/queries';
import { filtersConfig } from './ManageProcess/utils';
import { groupsFiltersConfig } from './manage-groups/utils';
import { queryProcessConfig, startProcess, updateFavorite } from './service';

const { ManageProcess } = CoreQueries;

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialLaunchpadHome = {
  groups: [],
  processes: [],
  favorites: [],
  progress: { processesLoaded: 0, processesTotal: 0 },
  loading: false,
  loaded: false,
  error: null,
  subgroupsFetched: {},
  /**
   * Epoch monotónico que invalida sagas obsoletas. Se incrementa con cada
   * `launchpadHomeStart` y cada `launchpadHomeReset`. Las sagas que
   * todavía están en vuelo de un epoch anterior (ej. una paginación de
   * la empresa anterior que termina después del cambio de tenant) no
   * deben tocar la slice nueva — comparan su epoch capturado contra el
   * actual y se silencian.
   */
  epoch: 0,
};

const initialState = {
  data: [],
  searchData: [],
  allGroups: [],
  favorites: [],
  processList: [],
  currentItem: {},
  newGroup: false,
  selectedItem: null,
  showGroupDetail: false,
  searchCriteria: null,
  pagination: meta,
  processListPagination: meta,
  activeTab: 'groups',
  favorite: false,
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
  // ... Lightweight favorites slice consumed by the Home dashboard
  // Quick Launch rail. Kept separate from the full Launchpad payload so
  // entering `/` doesn't trigger the heavier groups + processes batch.
  favoritesQuick: { items: [], loaded: false },
  // ... Launchpad (unified view)
  launchpadHome: initialLaunchpadHome,
  // ... start process
  validationErrors: [],
  processConfig: null,
  redirectTo: null,
};

const Model = {
  namespace: 'processes',
  state: initialState,
  effects: {
    *fetchData({ payload }, { call, put }) {
      try {
        const params = { route: 'process/groups', ...payload, ...ManageProcess.group };
        const res = yield call(payload.id ? api.getById : api.getAll, params);
        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchList({ payload }, { call, put }) {
      try {
        // `process/manage` is access-aware: the backend returns the
        // processes the caller can see based on their role and exposes
        // the per-record `canManage` flag so the UI can gate
        // edit/delete actions.
        const params = {
          route: 'process/manage',
          ...payload,
          ...ManageProcess.allowed,
          select: `${ManageProcess.allowed.select},canManage`,
        };
        const res = yield call(api.getAll, params);
        yield put({
          type: 'queryList',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchFavorites({ payload }, { call, put }) {
      try {
        // Backend defaults to 5 items when $top/$skip are omitted.
        const params = {
          route: 'process/favorites',
          page: 1,
          limit: 500,
          ...payload,
          ...ManageProcess.favorite,
        };
        const res = yield call(api.getAll, params);
        yield put({ type: 'queryFavorites', payload: res });
      } catch (e) {}
    },

    *fetchFavoritesQuick(_, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'process/favorites',
          page: 1,
          limit: 500,
          ...ManageProcess.favorite,
        });
        const items = Array.isArray(res) ? res : res?.items || [];
        yield put({ type: 'saveFavoritesQuick', payload: items });
      } catch (e) {
        yield put({ type: 'saveFavoritesQuick', payload: [] });
      }
    },

    *fetchConfig({ payload }, { call, put }) {
      try {
        const res = yield call(queryProcessConfig, payload);
        yield put({ type: 'queryConfig', payload: res });
      } catch (e) {
        yield put({ type: 'queryConfig' });
        history.replace('/processes');
      }
    },

    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });
        yield put({ type: 'updateLaunchpadFavorite', payload });

        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    /**
     * `takeLatest` + epoch guard.
     *
     *   1. `takeLatest`: si llega otro `fetchLaunchpadHome` mientras éste
     *      está en vuelo (refresh, cambio de tenant), redux-saga cancela
     *      la instancia anterior antes de su próximo `yield`.
     *   2. Epoch guard: aún así puede pasar que la respuesta del API
     *      llegue justo entre el `call` y el `put`, sin que takeLatest
     *      haya tenido oportunidad de cancelar. Antes de cada `put` de
     *      resultado, comparamos el epoch capturado con el actual; si
     *      cambió (porque hubo un reset por cambio de empresa/entorno),
     *      descartamos el resultado y no escribimos nada.
     *
     * Esta combinación elimina el bug de "error fantasma con peticiones
     * OK" después de cambiar empresa/entorno.
     */
    fetchLaunchpadHome: [
      function* ({ payload } = {}, { call, put, all, select, cancelled }) {
        yield put({ type: 'launchpadHomeStart' });
        const myEpoch = yield select((s) => s.processes?.launchpadHome?.epoch || 0);
        const isStale = function* () {
          const current = yield select((s) => s.processes?.launchpadHome?.epoch || 0);
          return current !== myEpoch;
        };
        try {
          const WORKFLOWS_FIRST_PAGE = 60;
          const WORKFLOWS_GROUPS_LIMIT = 500;

          // Admins (USER_ADMIN_ROLES) need to see inactive groups/processes
          // so they can manage them. Regular users only get the active set.
          const includeInactive = !!payload?.includeInactive;
          const activeFilter = includeInactive ? undefined : 'isActive eq true';

          // `process/groups` and `process/manage` are access-aware
          // endpoints: the backend already validates the caller's role
          // and returns only the records they're allowed to see —
          // admins receive the full tree (including inactive records),
          // regular users only receive what they can launch. That's why
          // we use the same routes for every role. The per-record
          // `canManage` flag is decided by the backend (true for
          // admins / per-process collaborators, false for regular
          // users).
          const groupsRoute = 'process/groups';
          const groupsQuery = ManageProcess.group;

          const itemsRoute = 'process/manage';
          const itemsQuery = {
            ...ManageProcess.allowed,
            select: `${ManageProcess.allowed.select},canManage`,
          };

          const [groupsRes, favoritesRes, firstPage] = yield all([
            call(api.getAll, {
              route: groupsRoute,
              page: 1,
              limit: WORKFLOWS_GROUPS_LIMIT,
              ...(activeFilter ? { filter: activeFilter } : {}),
              ...groupsQuery,
            }),
            call(api.getAll, {
              route: 'process/favorites',
              page: 1,
              limit: 500,
              ...(activeFilter ? { filter: activeFilter } : {}),
              ...ManageProcess.favorite,
            }),
            call(api.getAll, {
              route: itemsRoute,
              page: 1,
              limit: WORKFLOWS_FIRST_PAGE,
              ...(activeFilter ? { filter: activeFilter } : {}),
              ...itemsQuery,
            }),
          ]);

          // El epoch pudo cambiar mientras esperábamos las llamadas en
          // paralelo (cambio de tenant). Descartamos resultados obsoletos.
          if (yield* isStale()) return;

          const groups = groupsRes?.items || [];
          const favorites = Array.isArray(favoritesRes) ? favoritesRes : favoritesRes?.items || [];
          const firstProcesses = firstPage?.items || [];
          // The backend may use `totalCount` (OData passthrough) or `count`
          // depending on the endpoint; fall back across both to know when to
          // paginate in the background.
          const total = firstPage?.totalCount ?? firstPage?.count ?? firstProcesses.length;

          yield put({
            type: 'launchpadHomeSave',
            payload: {
              groups,
              favorites,
              processes: firstProcesses,
              progress: { processesLoaded: firstProcesses.length, processesTotal: total },
            },
          });

          if (firstProcesses.length < total) {
            yield put({
              type: 'fetchLaunchpadProcessesRest',
              payload: {
                page: 2,
                limit: WORKFLOWS_FIRST_PAGE,
                total,
                includeInactive,
                // Pasamos el epoch del fetch principal para que la
                // paginación de fondo herede el mismo guard.
                epoch: myEpoch,
              },
            });
          }
        } catch (e) {
          // 1) Si la saga fue cancelada por `takeLatest` (porque se
          //    disparó un nuevo fetch), redux-saga lanza una
          //    `SagaCancellationException` que cae acá. No es un error
          //    real, simplemente la saga vieja siendo descartada.
          // 2) Si el contexto cambió entre el call y el catch, también
          //    es una respuesta obsoleta — no la propagamos.
          if (yield cancelled()) return;
          if (yield* isStale()) return;
          yield put({ type: 'launchpadHomeError', payload: e?.message || 'error' });
        }
      },
      { type: 'takeLatest' },
    ],

    /**
     * Paginación de fondo. `takeLatest` + epoch guard heredado del fetch
     * principal: si entre páginas cambia el tenant (y la slice se
     * resetea), las páginas restantes ven que el epoch ya no coincide y
     * abortan en silencio — no escriben items obsoletos ni errores
     * fantasma sobre la nueva slice.
     */
    fetchLaunchpadProcessesRest: [
      function* ({ payload }, { call, put, select, cancelled }) {
        const {
          page: startPage = 2,
          limit = 60,
          total = 0,
          includeInactive = false,
          epoch: myEpoch = null,
        } = payload || {};
        const isStale = function* () {
          if (myEpoch == null) return false; // sin guard: comportamiento legacy.
          const current = yield select((s) => s.processes?.launchpadHome?.epoch || 0);
          return current !== myEpoch;
        };
        let page = startPage;
        let safety = 0;
        try {
          // Mirrors `fetchLaunchpadHome`: `process/manage` is
          // access-aware, so every role paginates the same endpoint.
          // The backend decides what to return based on the caller's
          // role and exposes the per-record `canManage` flag.
          const itemsRoute = 'process/manage';
          const itemsQuery = {
            ...ManageProcess.allowed,
            select: `${ManageProcess.allowed.select},canManage`,
          };
          // safety cap to prevent runaway loops (60 * 50 pages = 3000 procesos)
          while (safety < 50) {
            if (yield* isStale()) return;
            const res = yield call(api.getAll, {
              route: itemsRoute,
              page,
              limit,
              ...(includeInactive ? {} : { filter: 'isActive eq true' }),
              ...itemsQuery,
            });
            if (yield* isStale()) return;
            const items = res?.items || [];
            const count = res?.totalCount ?? res?.count ?? total;
            if (items.length === 0) break;

            yield put({ type: 'appendLaunchpadProcesses', payload: { items, total: count } });

            if (items.length < limit) break;
            page += 1;
            safety += 1;
          }
        } catch (e) {
          if (yield cancelled()) return;
          if (yield* isStale()) return;
          yield put({ type: 'launchpadHomeError', payload: e?.message || 'error' });
        }
      },
      { type: 'takeLatest' },
    ],

    *resetLaunchpadHome(_, { put }) {
      yield put({ type: 'launchpadHomeReset' });
    },

    /**
     * Lazy-loads the immediate subgroups of a given parent group.
     * The API's default listing returns only top-level groups, so we need an
     * explicit filter to fetch nested ones. Called the first time a group
     * with `groupsCount > 0` is expanded in the Launchpad.
     */
    *fetchLaunchpadSubgroups({ payload }, { call, put, select }) {
      const parentId = payload?.parentId;
      if (!parentId) return;

      const alreadyLoaded = yield select((state) => {
        const { subgroupsFetched = {} } = state.processes?.launchpadHome || {};
        return !!subgroupsFetched[parentId];
      });
      if (alreadyLoaded) return;

      try {
        // Filter only by parent id. `isActive` is omitted because some groups
        // that *host* active processes are themselves not flagged as active in
        // legacy data (the parent `/process/groups` list still returns their
        // ancestors). The client's `keepActive` guard drops any truly inactive
        // ones that do not have active children anyway.
        const res = yield call(api.getAll, {
          route: 'process/groups',
          page: 1,
          limit: 500,
          filter: `parentProcessGroupId eq ${parentId}`,
          ...ManageProcess.group,
        });
        const items = res?.items || [];
        yield put({
          type: 'mergeLaunchpadGroups',
          payload: { items, parentId },
        });
      } catch (e) {
        /* non-fatal: keep the flat list and the user can still open processes */
      }
    },

    *initProcess({ payload }, { put }) {
      yield put({ type: 'queryInit', payload });
    },

    *startProcess({ payload }, { call, put }) {
      try {
        const { processId, formId, ...params } = payload;
        const res = yield call(api.create, {
          route: `processEngine/start/${processId}/${formId}`,
          ...params,
        });

        if (res.processInstance) {
          SocketService.getInstance().send('joinGroup', {
            type: 'process-instance',
            groupId: _.toUpper(res.processInstance.id),
          });
        }

        yield put({ type: 'queryStart', payload: res });
      } catch (e) {
        // Error recovery: clear validation errors and reset submission state
        yield put({ type: 'queryStartError', payload: e });

        if (e?.validationErrors) {
          yield put({
            type: 'global/validationErrors',
            payload: e.validationErrors,
          });
        }
      }
    },

    *fetchGroup({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, {
          route: 'processGroup/getInfo',
          params: { select: 'id,name' },
          ...payload,
        });
        yield put({ type: 'queryGroup', payload: res });
      } catch (e) {
        history.goBack();
      }
    },

    *cleanupGroup({ payload }, { put }) {
      yield put({ type: 'queryGroup', payload });
    },

    *setActiveTab({ payload }, { put }) {
      yield put({ type: 'updateTab', payload });
    },

    *addNewProcessGroup({ payload }, { put }) {
      yield put({ type: 'newProcessGroup', payload });
    },

    *processGroupDetail({ payload }, { put }) {
      yield put({ type: 'showGroupDetail', payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *paginate({ payload }, { put }) {
      yield put({ type: 'queryPage', payload });
    },

    *hideAllForms(o, { put }) {
      yield put({ type: 'hideForms' });
    },

    *reset(o, { put }) {
      yield put({ type: 'resetState' });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'global/validationErrors' });
      yield put({ type: 'queryClean', payload });
    },

    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.processes);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload.items || [],
        favorite: false,
        pagination: {
          ...meta,
          ...toPaginate({ ...state.pagination, ...payload }),
        },
      };
    },

    queryList(state, { payload }) {
      return {
        ...state,
        favorite: false,
        processList: payload.items || [],
        data: payload.items || [],
        processListPagination: {
          ...state.processListPagination,
          ...toPaginate({ ...state.processListPagination, ...payload }),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorite: false,
        favorites: payload,
      };
    },

    saveFavoritesQuick(state, { payload }) {
      return {
        ...state,
        favoritesQuick: { items: payload || [], loaded: true },
      };
    },

    launchpadHomeStart(state) {
      const prev = state.launchpadHome || initialLaunchpadHome;
      return {
        ...state,
        launchpadHome: {
          ...prev,
          loading: true,
          error: null,
          epoch: (prev.epoch || 0) + 1,
        },
      };
    },

    launchpadHomeSave(state, { payload }) {
      return {
        ...state,
        launchpadHome: {
          ...(state.launchpadHome || initialLaunchpadHome),
          groups: payload.groups || [],
          favorites: payload.favorites || [],
          processes: payload.processes || [],
          progress: payload.progress || { processesLoaded: 0, processesTotal: 0 },
          loading: false,
          loaded: true,
          error: null,
        },
      };
    },

    appendLaunchpadProcesses(state, { payload }) {
      const prev = state.launchpadHome || initialLaunchpadHome;
      const nextProcesses = [...prev.processes, ...(payload.items || [])];
      return {
        ...state,
        launchpadHome: {
          ...prev,
          processes: nextProcesses,
          progress: {
            processesLoaded: nextProcesses.length,
            processesTotal: payload.total || nextProcesses.length,
          },
        },
      };
    },

    launchpadHomeError(state, { payload }) {
      return {
        ...state,
        launchpadHome: {
          ...(state.launchpadHome || initialLaunchpadHome),
          loading: false,
          error: payload,
        },
      };
    },

    launchpadHomeReset(state) {
      const prev = state.launchpadHome || initialLaunchpadHome;
      return {
        ...state,
        // Conservamos el epoch incrementado para invalidar cualquier saga
        // pendiente que haya capturado el epoch anterior.
        launchpadHome: { ...initialLaunchpadHome, epoch: (prev.epoch || 0) + 1 },
      };
    },

    mergeLaunchpadGroups(state, { payload }) {
      const prev = state.launchpadHome || initialLaunchpadHome;
      const incoming = payload?.items || [];
      const parentId = payload?.parentId;
      if (incoming.length === 0 && !parentId) return state;

      const seen = new Set((prev.groups || []).map((g) => g.id));
      const merged = [...(prev.groups || [])];
      incoming.forEach((g) => {
        if (!seen.has(g.id)) merged.push(g);
      });

      return {
        ...state,
        launchpadHome: {
          ...prev,
          groups: merged,
          subgroupsFetched: {
            ...(prev.subgroupsFetched || {}),
            ...(parentId ? { [parentId]: true } : {}),
          },
        },
      };
    },

    /**
     * Sync the Launchpad slice when a process is activated/deactivated
     * from any kebab (legacy /processes table or the new module home).
     * Keeps the active-only list in sync so the tile re-styles or
     * disappears the moment the saga settles, without forcing a refetch.
     */
    updateLaunchpadProcessActive(state, { payload }) {
      const prev = state.launchpadHome || initialLaunchpadHome;
      const next = !!payload.isActive;
      const processes = prev.processes.map((p) =>
        p.id === payload.id ? { ...p, isActive: next } : p,
      );
      const favorites = prev.favorites.map((f) =>
        f.id === payload.id ? { ...f, isActive: next } : f,
      );
      return {
        ...state,
        launchpadHome: { ...prev, processes, favorites },
      };
    },

    updateLaunchpadFavorite(state, { payload }) {
      const prev = state.launchpadHome || initialLaunchpadHome;
      const nextIsFavorite = !payload.isFavorite;
      const processes = prev.processes.map((p) =>
        p.id === payload.id ? { ...p, isFavorite: nextIsFavorite } : p,
      );
      let favorites;
      if (nextIsFavorite) {
        const target = processes.find((p) => p.id === payload.id);
        favorites =
          target && !prev.favorites.some((f) => f.id === payload.id)
            ? [...prev.favorites, target]
            : prev.favorites;
      } else {
        favorites = prev.favorites.filter((f) => f.id !== payload.id);
      }
      return {
        ...state,
        launchpadHome: { ...prev, processes, favorites },
      };
    },

    queryInit(state, { payload }) {
      return {
        ...state,
        redirectTo: `/processes/start?ProcessId=${payload}`,
      };
    },

    queryConfig(state, { payload }) {
      return {
        ...state,
        activeTab: 'groups',
        processConfig: !!payload && {
          ...payload,
          behaviorCases: formFieldBehaviorsMapping(payload),
        },
      };
    },

    queryStart(state, { payload }) {
      const { command, redirectRoute, processInstance, autofire } = payload;
      if (autofire && command.command !== CoreEnums.RequestDetailCommandType.Details) {
        //  set temporally variable to persist the auto command execution
        sessionStorage.setItem('x-autoFireCommand', JSON.stringify(command));
      }

      setTimeout(() => {
        // flag in the auto open from the start process
        sessionStorage.setItem('x-autoOpenEditOperation', 1);
      }, [500]);

      return {
        ...state,
        processInstanceId: processInstance?.id,
        redirectToRoute: redirectRoute,
      };
    },

    queryStartError(state, { payload }) {
      // Error recovery: maintain form state for retry
      return {
        ...state,
        validationErrors: payload?.validationErrors || [],
      };
    },

    newProcessGroup(state, { payload }) {
      return {
        ...state,
        newGroup: payload.state,
        selectedItem: payload.item,
      };
    },

    showGroupDetail(state, { payload }) {
      return { ...state, showGroupDetail: payload };
    },

    queryGroup(state, { payload }) {
      return {
        ...state,
        currentItem: payload,
      };
    },

    updateTab(state, { payload }) {
      return {
        ...state,
        activeTab: payload,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    queryPage(state, { payload }) {
      const { data, searchCriteria, searchData } = state;
      const totalCount = searchCriteria ? searchData.length : data.length;
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...toPaginate({ ...payload, totalCount }),
        },
      };
    },

    querySetFavorite(state, { payload }) {
      const prevQuick = state.favoritesQuick || { items: [], loaded: false };
      const wasFavorite = !!payload.isFavorite;
      const updatedQuick = wasFavorite
        ? prevQuick.items.filter((item) => item.id !== payload.id)
        : prevQuick.items.some((item) => item.id === payload.id)
        ? prevQuick.items
        : [
            ...prevQuick.items,
            // best-effort: copy minimal data from `data` if available so the
            // Quick Launch rail can render immediately without a refetch.
            ...(state.data.find((d) => d.id === payload.id)
              ? [{ ...state.data.find((d) => d.id === payload.id), isFavorite: true }]
              : []),
          ];

      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isFavorite: !payload.isFavorite } : item,
        ),
        favorites: payload.isFavorite
          ? state.favorites.filter((item) => item.id !== payload.id)
          : state.favorites,
        favoritesQuick: { ...prevQuick, items: updatedQuick },
      };
    },

    markFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: true,
      };
    },

    clearFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: false,
      };
    },

    hideForms(state) {
      return {
        ...state,
        newGroup: false,
        showGroupDetail: false,
      };
    },

    queryClean(state, { payload }) {
      switch (payload) {
        case 'redirect':
          return {
            ...state,
            processList: [],
            processListPagination: meta,
            redirectTo: null,
            searchCriteria: '',
          };
        case 'start-process':
          return {
            ...state,
            validationErrors: [],
            processConfig: null,
            redirectTo: null,
            processInstanceId: null,
            redirectToRoute: null,
          };
        default:
          return {
            ...initialState,
            launchpadHome: state?.launchpadHome || initialLaunchpadHome,
          };
      }
    },

    resetState(state) {
      // Preserve the Launchpad slice: it lives in the same namespace but is
      // independent of the legacy /processes flows (which dispatch this reset
      // on unmount). Wiping it caused the /launchpad view to render empty
      // after navigating back from /processes/start.
      return {
        ...initialState,
        launchpadHome: state?.launchpadHome || initialLaunchpadHome,
      };
    },
  },
};

export default Model;
